import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin, Search, Loader2 } from 'lucide-react';

/**
 * AddressAutocomplete - Google Places Autocomplete Component
 * Reusable address search with autocomplete functionality
 */
export function AddressAutocomplete({ 
  onAddressSelect, 
  language = 'fr',
  placeholder,
  initialValue = '',
  label = true
}) {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceRef = useRef(null);

  // Initialize Google Places services
  useEffect(() => {
    const initGooglePlaces = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        const dummyDiv = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
        return true;
      }
      return false;
    };

    if (!initGooglePlaces()) {
      const checkInterval = setInterval(() => {
        if (initGooglePlaces()) {
          clearInterval(checkInterval);
        }
      }, 100);
      setTimeout(() => clearInterval(checkInterval), 10000);
    }
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddress = useCallback(async (searchQuery) => {
    if (searchQuery.length < 3 || !autocompleteService.current) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    
    try {
      autocompleteService.current.getPlacePredictions(
        {
          input: searchQuery,
          componentRestrictions: { country: 'fr' },
          types: ['establishment', 'geocode'],
          language: 'fr'
        },
        (predictions, status) => {
          setIsLoading(false);
          
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map(prediction => ({
              place_id: prediction.place_id,
              display_name: prediction.description,
              main_text: prediction.structured_formatting?.main_text || '',
              secondary_text: prediction.structured_formatting?.secondary_text || '',
              types: prediction.types || []
            })));
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
          }
        }
      );
    } catch (error) {
      console.error('Google Places search error:', error);
      setIsLoading(false);
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchAddress(value);
    }, 300);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.display_name);
    setShowSuggestions(false);
    setIsLoading(true);
    
    if (placesService.current && suggestion.place_id) {
      placesService.current.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ['address_components', 'formatted_address', 'geometry', 'name']
        },
        (place, status) => {
          setIsLoading(false);
          
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            let streetNumber = '';
            let route = '';
            let postalCode = '';
            let city = '';
            
            place.address_components?.forEach(component => {
              const types = component.types;
              if (types.includes('street_number')) {
                streetNumber = component.long_name;
              }
              if (types.includes('route')) {
                route = component.long_name;
              }
              if (types.includes('postal_code')) {
                postalCode = component.long_name;
              }
              if (types.includes('locality')) {
                city = component.long_name;
              }
            });
            
            const fullAddress = streetNumber && route 
              ? `${streetNumber} ${route}` 
              : (route || place.formatted_address || suggestion.display_name);
            
            onAddressSelect({
              address: fullAddress,
              postal_code: postalCode,
              city: city,
              full_address: place.formatted_address || suggestion.display_name
            });
          } else {
            onAddressSelect({
              address: suggestion.display_name,
              postal_code: '',
              city: '',
              full_address: suggestion.display_name
            });
          }
        }
      );
    } else {
      setIsLoading(false);
      onAddressSelect({
        address: suggestion.display_name,
        postal_code: '',
        city: '',
        full_address: suggestion.display_name
      });
    }
  };

  const getPlaceIcon = (types) => {
    if (types.includes('supermarket') || types.includes('grocery_or_supermarket')) return '🛒';
    if (types.includes('store') || types.includes('shopping_mall')) return '🏪';
    if (types.includes('restaurant') || types.includes('food')) return '🍽️';
    if (types.includes('street_address') || types.includes('route')) return '🛣️';
    if (types.includes('locality') || types.includes('administrative_area_level_1')) return '🏛️';
    if (types.includes('establishment')) return '🏢';
    return '📍';
  };

  const defaultPlaceholder = language === 'fr' 
    ? 'Rechercher une adresse...' 
    : 'Search address...';

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <Label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5 block">
          <MapPin className="w-4 h-4 inline mr-1" />
          {language === 'fr' ? 'Rechercher une adresse' : 'Search address'}
        </Label>
      )}
      <div className="relative">
        <Input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={placeholder || defaultPlaceholder}
          className="pl-10"
          data-testid="address-autocomplete-input"
        />
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        {isLoading && (
          <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" />
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-start gap-3 border-b border-slate-100 dark:border-slate-700 last:border-0 transition-colors"
            >
              <span className="text-xl flex-shrink-0 mt-0.5">
                {getPlaceIcon(suggestion.types)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 dark:text-white truncate">
                  {suggestion.main_text}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                  {suggestion.secondary_text}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
